import { autoinject } from "aurelia-framework";
import { Router, RouterConfiguration, Redirect } from "aurelia-router";
import { User } from "oidc-client";
import { OpenIdConnect, OpenIdConnectRoles } from "aurelia-open-id-connect";
import { applicationLogger } from "../logging/logger";
import "@mdi/font/css/materialdesignicons.css";
import { AppSettings } from "./app-settings";
import { AureliaApplicationSettings } from "../models/aurelia-application-settings";

@autoinject
export class App {

    router: Router;
    user: User;

    constructor(private openIdConnect: OpenIdConnect, private appSettings: AppSettings, private applicationSettings: AureliaApplicationSettings) { }

    attached() {
        this.openIdConnect.observeUser((user: User) => this.onUserChanged(user));
    }

    private onUserChanged(user: User) {
        this.user = user;
        if (!this.user || this.user.expired) {
            this.openIdConnect.login();
        }
    }

    configureRouter(config: RouterConfiguration, router: Router) {

        this.router = router;

        // switch from hash (#) to slash (/) navigation
        config.options.pushState = true;
        config.title = "Sanistar Remote Admin";
        config.map([
            {
                route: 'reports', name: 'reports',
                moduleId: "../pages/reports/reports",
                nav: true,
                settings: { icon: "bar_chart", roles: [OpenIdConnectRoles.Authenticated], t: "reports" },
                requireAuthorization: true,
                roles: ["User"]                
            },
            {
                route: ['', 'tenants'],
                name: "tenants",
                nav: true,
                settings: { icon: "work", roles: [OpenIdConnectRoles.Authenticated], t: "customers" },
                moduleId: "../pages/tenants/tenants",
                requireAuthorization: true,
                roles: ["GlobalAdministrator", "TenantAdministrator"]
            },
            {
                route: ['lookup'],
                name: "lookup",
                nav: true,
                settings: { icon: "search", roles: [OpenIdConnectRoles.Authenticated], t: "lookup" },
                moduleId: "../pages/tenants/transactions/transactions-lookup",
                requireAuthorization: true,
                roles: ["GlobalAdministrator"]
            },
            {
                route: 'tenants/:tenantId/transactions',
                name: 'transactions',
                moduleId: "../pages/tenants/transactions/transactions",
                nav: false,
                settings: { roles: [OpenIdConnectRoles.Authenticated] },
                requireAuthorization: true,
                roles: ["GlobalAdministrator", "TenantAdministrator"]
            },
            {
                route: 'tenants/:tenantId/kiosks',
                name: 'kiosks',
                moduleId: "../pages/tenants/kiosks/kiosks",
                nav: false,
                settings: { roles: [OpenIdConnectRoles.Authenticated] },
                requireAuthorization: true,
                roles: ["GlobalAdministrator", "TenantAdministrator"]
            },
            {
                route: 'tenants/:tenantId/users',
                name: 'users',
                moduleId: "../pages/tenants/users/users",
                nav: false,
                settings: { roles: [OpenIdConnectRoles.Authenticated] },
                requireAuthorization: true,
                roles: ["GlobalAdministrator", "TenantAdministrator"]
            },
            {
                route: '/unauthorized',
                name: 'unauthorized',
                moduleId: "../pages/authentication/unauthorized",
                nav: false,
            },
            {
                route: `${this.applicationSettings.Authority}`,
                name: 'redirect-to-authority-login',
                title: 'Logout',
                nav: false,
                requireAuthorization: false,
                navigationStrategy: () => undefined
            }

        ]);

        const handleUnknownRoutes = (instruction) => {
            return { route: 'not-found', moduleId: '../pages/not-found' };
        }
        config.mapUnknownRoutes(handleUnknownRoutes);

        config.addPipelineStep('authorize', AuthorizeStep);
        this.openIdConnect.configure(config);
    }
}

@autoinject
class AuthorizeStep {
    user: User;
    constructor(private openIdConnect: OpenIdConnect) {
        this.openIdConnect.observeUser((user: User) => this.onUserChanged(user));
    }

    private onUserChanged(user: User) {
        this.user = user;
    }

    get roles() {
        if (this.user) {
            return this.user.profile.role;
        }
        return [];

    }

    async run(navigationInstruction, next) {

        if (navigationInstruction.config.requireAuthorization && navigationInstruction.config.roles) {
            for (var i = 0; i < navigationInstruction.config.roles.length; i++) {

                if (!this.user || this.user.expired) {
                    return next();
                }

                if (this.roles.includes(navigationInstruction.config.roles[i])) {
                    return next();
                }

            }

            applicationLogger.warn('not authorized');
            return next.cancel(new Redirect('unauthorized'));
        }

        navigationInstruction.getAllInstructions();
        return next();
    }
}


